import { List, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  Ordering,
  PivotColumnResponse,
  SortableField,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectSorts } from "../../../../store/selectors/fieldsStateSelectors";
import { selectDimensionsForCurrentReportType } from "../../../../store/selectors/metaDataSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import { buildPivotColumnMetadataLookup } from "../../../viewer/pivot/utils/pivotColumnMetadata.ts";
import SelectSortFieldInput from "./SelectSortFieldInput";
import SortFieldItem from "./SortFieldItem";
import { getPivotSortFilterMap } from "./pivotSortDisplayHelper.ts";

interface Props {
  availableFields: SortableField[];
  pivotColumns?: PivotColumnResponse[];
  readonlyMode: boolean;
}

export const SortFieldsContainer = ({ availableFields, pivotColumns = [], readonlyMode }: Props) => {
  const sortsAreaValues = useSelector(selectSorts);
  const dimensions = useSelector(selectDimensionsForCurrentReportType);
  const pivotColumnMetadataLookup = useMemo(() => buildPivotColumnMetadataLookup(pivotColumns), [pivotColumns]);

  const dimensionCaptionsMap = useMemo(() => {
    return dimensions.reduce(
      (acc, dimension) => {
        acc[dimension.name] = dimension.caption;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [dimensions]);

  const dispatch = useAppDispatch();

  const onChangeSorting = useCallback(
    (field: SortField, ordering: Ordering | undefined) => {
      dispatch(fieldsStateActions.updateSortingConfig({ field, changes: { ordering } }));
    },
    [dispatch]
  );

  const onRemoveItem = useCallback(
    (field: SortField) => {
      dispatch(fieldsStateActions.removeSorting(field));
    },
    [dispatch]
  );

  return (
    <Stack sx={{ flex: 1, gap: ".5rem" }}>
      <Typography variant="subtitle2">Sort</Typography>
      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.grey,
          minHeight: "90px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "2px",
          p: ".5rem",
          gap: ".5rem",
        })}
      >
        {sortsAreaValues.length > 0 && (
          <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
            {sortsAreaValues.map((item) => {
              return (
                <SortFieldItem
                  key={`${item.meta.name}_${item.config.pivotColumnId ?? ""}`}
                  field={item}
                  onRemoveItem={() => onRemoveItem(item)}
                  onChangeSorting={(order) => onChangeSorting(item, order)}
                  filterMap={getPivotSortFilterMap(item, pivotColumnMetadataLookup, dimensionCaptionsMap)}
                  readonlyMode={readonlyMode}
                />
              );
            })}
          </List>
        )}
        <SelectSortFieldInput availableFields={availableFields} disabled={readonlyMode} />
      </Stack>
    </Stack>
  );
};

export default SortFieldsContainer;
